import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Modal,
  Table,
  Input,
  Tag,
  Tooltip,
  Badge,
  Space,
  Progress,
  message,
  Tabs,
  Popconfirm,
} from "antd";
import { useSelector } from "react-redux";
import {
  deleteEmailCampaign,
  sendEmailCampaign,
  getEmailCampaignsPaginated,
  pauseEmailCampaign,
  resumeEmailCampaign,
  stopEmailCampaign,
} from "@api/email/emailCampaign";
import Cookies from "js-cookie";
import { useQuery, useQueryClient } from "react-query";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  StopOutlined,
  CloudOutlined,
} from "@ant-design/icons";

const { Search } = Input;
const { TabPane } = Tabs;

// Domain color mapping
const domainColors = {
  gmail: "#DB4437",
  yahoo: "#720e9e",
  microsoft: "#00A4EF",
  apple: "#555555",
  proton: "#6D4AFF",
  zoho: "#E42322",
  other: "#666666",
};

// Format the countdown time
const formatCountdown = (targetDate) => {
  if (!targetDate) return null;

  const now = new Date();
  const target = new Date(targetDate);

  // If the target date is in the past, return null
  if (now >= target) return null;

  const diffMs = target - now;
  const diffSeconds = Math.floor(diffMs / 1000);

  if (diffSeconds < 60) {
    return `${diffSeconds}s`;
  }

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) {
    return `${diffMinutes}m ${diffSeconds % 60}s`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours}h ${diffMinutes % 60}m`;
  }

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ${diffHours % 24}h`;
};

const EmailCampaignsList = ({ history }) => {
  const { user } = useSelector((state) => state.auth);
  const queryClient = useQueryClient();

  // Main state
  const [emailCampaigns, setEmailCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [statusCounts, setStatusCounts] = useState({
    draft: 0,
    scheduled: 0,
    sending: 0,
    paused: 0,
    completed: 0,
    stopped: 0,
    total: 0,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    totalEmailCampaigns: 0,
  });
  const [sentCampaigns, setSentCampaigns] = useState(new Set());
  const [actionLoading, setActionLoading] = useState({});

  // Check for send permission
  const canSendCampaigns = Cookies.get("snd123") !== undefined;

  // Add a timer for countdown updates
  const [tick, setTick] = useState(0);
  useEffect(() => {
    // Update countdown displays every second
    const timer = setInterval(() => {
      setTick((t) => t + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Effect to detect if any campaign needs to be started (scheduled time reached)
  useEffect(() => {
    const now = new Date();
    const hasScheduledCampaign = emailCampaigns.some(
      (campaign) =>
        campaign.status === "draft" &&
        campaign.scheduledFor &&
        now >= new Date(campaign.scheduledFor)
    );

    if (hasScheduledCampaign) {
      queryClient.invalidateQueries("emailCampaigns");
    }
  }, [tick, emailCampaigns, queryClient]);

  // Use React Query for auto-refreshing data
  const fetchEmailCampaignsData = useCallback(async () => {
    try {
      const res = await getEmailCampaignsPaginated(
        pagination.current,
        pagination.pageSize,
        searchText,
        activeTab === "all" ? undefined : activeTab
      );
      return res.data;
    } catch (err) {
      console.error("Fetch email campaigns error", err);
      throw err;
    }
  }, [pagination.current, pagination.pageSize, searchText, activeTab]);

  const { data, isLoading, error } = useQuery(
    [
      "emailCampaigns",
      pagination.current,
      pagination.pageSize,
      searchText,
      activeTab,
    ],
    fetchEmailCampaignsData,
    {
      refetchInterval: 10000, // Refresh every 10 seconds
      refetchOnWindowFocus: true,
      onSuccess: (data) => {
        const campaigns = data.emailCampaigns || [];

        // Special logic to check for scheduled campaigns that should be updated
        const now = new Date();
        let changesDetected = false;

        // Detect any status changes in scheduled campaigns
        campaigns.forEach((campaign) => {
          if (campaign.status === "draft" && campaign.scheduledFor) {
            const scheduledTime = new Date(campaign.scheduledFor);
            if (now >= scheduledTime) {
              changesDetected = true;
            }
          }
        });

        // If scheduled time has passed for any campaign, force a refetch to get updated status
        if (changesDetected) {
          setTimeout(
            () => queryClient.invalidateQueries("emailCampaigns"),
            5000
          );
        }

        setEmailCampaigns(campaigns);
        setStatusCounts(data.statusCounts || {});
        setPagination((p) => ({
          ...p,
          totalEmailCampaigns: data.paginationData?.totalEmailCampaigns,
          current: data.paginationData?.page,
          pageSize: data.paginationData?.perPage,
        }));
      },
    }
  );

  // Effect to update loading state based on React Query's isLoading
  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  // Initial load
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const page = parseInt(searchParams.get("page") || "1", 10);
    const pageSize = parseInt(searchParams.get("pageSize") || "10", 10);
    const search = searchParams.get("search") || "";
    const status = searchParams.get("status") || "all";

    setSearchText(search);
    setActiveTab(status);
    setPagination({ current: page, pageSize, totalEmailCampaigns: 0 });
  }, []);

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPagination((prev) => ({ ...prev, current: 1 }));

    const searchParams = new URLSearchParams();
    searchParams.set("page", "1");
    searchParams.set("pageSize", pagination.pageSize);
    searchParams.set("status", tab);
    if (searchText) searchParams.set("search", searchText);
    history.push({ search: searchParams.toString() });
  };

  // Handle pagination
  const handlePaginationChange = (page, pageSize) => {
    const searchParams = new URLSearchParams();
    searchParams.set("page", page);
    searchParams.set("pageSize", pageSize);
    if (searchText) searchParams.set("search", searchText);
    searchParams.set("status", activeTab);

    history.push({ search: searchParams.toString() });
    setPagination((prev) => ({ ...prev, current: page, pageSize }));
  };

  // Handle search
  const handleSearch = (searchValue) => {
    setSearchText(searchValue);
    setPagination((prev) => ({ ...prev, current: 1 }));

    const searchParams = new URLSearchParams();
    searchParams.set("page", "1");
    searchParams.set("pageSize", pagination.pageSize);
    searchParams.set("status", activeTab);
    if (searchValue) searchParams.set("search", searchValue);
    history.push({ search: searchParams.toString() });
  };

  // Campaign actions
  const handleEmailCampaignUpdate = (id) => {
    const basePath =
      user.role === "superadmin" ? "/super-admin" : "/marketing-admin";
    history.push(`${basePath}/email/email-campaigns/${id}/update`);
  };

  const handleEmailCampaignDelete = (id) => {
    Modal.confirm({
      title: "Are you sure you want to delete this email campaign?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await deleteEmailCampaign(id);
          queryClient.invalidateQueries("emailCampaigns");
        } catch (error) {
          console.error("Error deleting email campaign", error);
        }
      },
    });
  };

  const handleEmailCampaignAnalytics = (id) => {
    const basePath =
      user.role === "superadmin" ? "/super-admin" : "/marketing-admin";
    history.push(`${basePath}/email/email-campaigns/${id}/analytics`);
  };

  const handleSendEmailCampaign = async (id) => {
    setSentCampaigns((prev) => new Set(prev).add(id));
    try {
      await sendEmailCampaign(id);
      queryClient.invalidateQueries("emailCampaigns");
    } catch (error) {
      console.error("Error sending email campaign", error);
    }
  };

  // Campaign control actions
  const handlePauseEmailCampaign = async (id) => {
    setActionLoading((prev) => ({ ...prev, [id]: "pause" }));
    try {
      await pauseEmailCampaign(id);
      message.success("Campaign paused successfully");
      queryClient.invalidateQueries("emailCampaigns");
    } catch (error) {
      console.error("Error pausing email campaign", error);
      message.error("Failed to pause campaign");
    } finally {
      setActionLoading((prev) => ({ ...prev, [id]: null }));
    }
  };

  const handleResumeEmailCampaign = async (id) => {
    setActionLoading((prev) => ({ ...prev, [id]: "resume" }));
    try {
      await resumeEmailCampaign(id);
      message.success("Campaign resumed successfully");
      queryClient.invalidateQueries("emailCampaigns");
    } catch (error) {
      console.error("Error resuming email campaign", error);
      message.error("Failed to resume campaign");
    } finally {
      setActionLoading((prev) => ({ ...prev, [id]: null }));
    }
  };

  const handleStopEmailCampaign = async (id) => {
    setActionLoading((prev) => ({ ...prev, [id]: "stop" }));
    try {
      await stopEmailCampaign(id);
      message.success("Campaign stopped permanently");
      queryClient.invalidateQueries("emailCampaigns");
    } catch (error) {
      console.error("Error stopping email campaign", error);
      message.error("Failed to stop campaign");
    } finally {
      setActionLoading((prev) => ({ ...prev, [id]: null }));
    }
  };

  // Table columns configuration
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name) => (
        <Tooltip title={name}>
          <span className="campaign-name">
            {name?.length > 25 ? `${name.substring(0, 25)}...` : name}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
      render: (subject) => (
        <Tooltip title={subject}>
          <span className="campaign-subject">
            {subject?.length > 25 ? `${subject.substring(0, 25)}...` : subject}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => {
        let color = "default";
        let text = status;
        let icon = null;
        const countdown =
          record.scheduledFor && status === "draft"
            ? formatCountdown(record.scheduledFor)
            : null;

        switch (status) {
          case "completed":
            color = "success";
            icon = <CheckCircleOutlined />;
            break;
          case "sending":
            color = "processing";
            // Don't use spinning icon if we have progress data
            icon = record.progress > 0 ? null : <SyncOutlined spin />;
            break;
          case "paused":
            color = "warning";
            icon = <PauseCircleOutlined />;
            break;
          case "stopped":
            color = "error";
            icon = <StopOutlined />;
            break;
          case "draft":
            if (record.scheduledFor) {
              color = "warning";
              text = "Scheduled";
              icon = <ClockCircleOutlined />;
            }
            break;
        }

        return (
          <Space direction="vertical">
            {status === "sending" && record.progress > 0 ? (
              <Tooltip title={`${Math.round(record.progress)}% complete`}>
                <Tag
                  color="processing"
                  style={{
                    padding: "2px 8px",
                    display: "inline-flex",
                    alignItems: "center",
                    height: "24px",
                  }}
                >
                  <span style={{ marginRight: 8, fontWeight: "bold" }}>
                    SENDING
                  </span>
                  <Badge
                    count={`${Math.round(record.progress)}%`}
                    style={{
                      backgroundColor: "#1890ff",
                      fontSize: "11px",
                      padding: "0 4px",
                      height: "16px",
                      lineHeight: "16px",
                    }}
                  />
                </Tag>
              </Tooltip>
            ) : (
              <Tag color={color} icon={icon}>
                {text.toUpperCase()}
              </Tag>
            )}
            {countdown && (
              <Tooltip title={`Scheduled to start in ${countdown}`}>
                <Badge
                  status="processing"
                  text={<small>Starts in {countdown}</small>}
                />
              </Tooltip>
            )}
            {record.progress > 0 &&
              record.progress < 100 &&
              status !== "sending" && (
                <Progress
                  type="circle"
                  percent={Math.round(record.progress)}
                  width={20}
                />
              )}
            {record.successRate > 0 && (
              <Tooltip title="Success Rate">
                <span style={{ color: "#52c41a" }}>
                  {Math.round(record.successRate)}%
                </span>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: "Scheduled For",
      dataIndex: "scheduledFor",
      key: "scheduledFor",
      render: (date) => (date ? new Date(date).toLocaleString() : "-"),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: "Action",
      key: "action",
      width: 420,
      render: (_, record) => (
        <Space wrap>
          <Button
            type="primary"
            size="small"
            onClick={() => handleEmailCampaignUpdate(record._id)}
            disabled={["sending", "paused"].includes(record.status)}
          >
            Edit
          </Button>

          <Button
            type="danger"
            size="small"
            onClick={() => handleEmailCampaignDelete(record._id)}
            disabled={["sending", "paused"].includes(record.status)}
          >
            Delete
          </Button>

          <Button
            type="default"
            size="small"
            onClick={() => handleEmailCampaignAnalytics(record._id)}
          >
            Analytics
          </Button>

          {record.status === "draft" &&
            canSendCampaigns &&
            !sentCampaigns.has(record._id) && (
              <Button
                type="default"
                size="small"
                onClick={() => handleSendEmailCampaign(record._id)}
              >
                Send
              </Button>
            )}

          {record.status === "sending" && (
            <>
              <Button
                type="warning"
                size="small"
                icon={<PauseCircleOutlined />}
                loading={actionLoading[record._id] === "pause"}
                onClick={() => handlePauseEmailCampaign(record._id)}
              >
                Pause
              </Button>

              <Popconfirm
                title="Are you sure you want to stop this campaign permanently?"
                onConfirm={() => handleStopEmailCampaign(record._id)}
                okText="Yes"
                cancelText="No"
                okButtonProps={{ danger: true }}
              >
                <Button
                  danger
                  size="small"
                  icon={<StopOutlined />}
                  loading={actionLoading[record._id] === "stop"}
                >
                  Stop
                </Button>
              </Popconfirm>
            </>
          )}

          {record.status === "paused" && (
            <>
              <Button
                type="primary"
                size="small"
                icon={<PlayCircleOutlined />}
                loading={actionLoading[record._id] === "resume"}
                onClick={() => handleResumeEmailCampaign(record._id)}
              >
                Resume
              </Button>

              <Popconfirm
                title="Are you sure you want to stop this campaign permanently?"
                onConfirm={() => handleStopEmailCampaign(record._id)}
                okText="Yes"
                cancelText="No"
                okButtonProps={{ danger: true }}
              >
                <Button
                  danger
                  size="small"
                  icon={<StopOutlined />}
                  loading={actionLoading[record._id] === "stop"}
                >
                  Stop
                </Button>
              </Popconfirm>
            </>
          )}

          {record.status !== "draft" && (
            <Button
              type="default"
              size="small"
              onClick={() => {
                const basePath =
                  user.role === "superadmin"
                    ? "/super-admin"
                    : "/marketing-admin";
                history.push(
                  `${basePath}/email/email-campaigns/${record._id}/sent-emails`
                );
              }}
            >
              View Sent Emails
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card style={{ borderRadius: "0", height: "100vh", overflowY: "hidden" }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <h3>Email Campaigns</h3>
        </Col>
        <Col>
          <Space size="middle">
            <Button
              type="ghost"
              onClick={() => {
                if (user.role === "superadmin") {
                  history.push("/super-admin/email/users");
                }
              }}
            >
              Manage Users
            </Button>

            <Button
              type="ghost"
              onClick={() => {
                const basePath =
                  user.role === "superadmin"
                    ? "/super-admin"
                    : "/marketing-admin";
                history.push(`${basePath}/email/email-sequences`);
              }}
            >
              Email Sequences
            </Button>

            <Button
              type="ghost"
              onClick={() => {
                const basePath =
                  user.role === "superadmin"
                    ? "/super-admin"
                    : "/marketing-admin";
                history.push(`${basePath}/email/html-templates`);
              }}
            >
              HTML Templates
            </Button>

            <Button
              type="primary"
              onClick={() => {
                const basePath =
                  user.role === "superadmin"
                    ? "/super-admin"
                    : "/marketing-admin";
                history.push(`${basePath}/email/email-campaigns/create`);
              }}
            >
              Create Campaign
            </Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col span={24}>
          <Card>
            <Tabs activeKey={activeTab} onChange={handleTabChange}>
              <TabPane
                tab={
                  <span>
                    All Campaigns
                    <Badge
                      count={statusCounts.total}
                      style={{ marginLeft: 8 }}
                    />
                  </span>
                }
                key="all"
              />
              <TabPane
                tab={
                  <span>
                    Draft
                    <Badge
                      count={statusCounts.draft}
                      style={{ marginLeft: 8 }}
                    />
                  </span>
                }
                key="draft"
              />
              <TabPane
                tab={
                  <span>
                    Scheduled
                    <Badge
                      count={statusCounts.scheduled}
                      style={{ marginLeft: 8 }}
                    />
                  </span>
                }
                key="scheduled"
              />
              <TabPane
                tab={
                  <span>
                    Sending
                    <Badge
                      count={statusCounts.sending}
                      style={{ marginLeft: 8 }}
                    />
                  </span>
                }
                key="sending"
              />
              <TabPane
                tab={
                  <span>
                    Paused
                    <Badge
                      count={statusCounts.paused}
                      style={{ marginLeft: 8 }}
                    />
                  </span>
                }
                key="paused"
              />
              <TabPane
                tab={
                  <span>
                    Completed
                    <Badge
                      count={statusCounts.completed}
                      style={{ marginLeft: 8 }}
                    />
                  </span>
                }
                key="completed"
              />
              <TabPane
                tab={
                  <span>
                    Stopped
                    <Badge
                      count={statusCounts.stopped}
                      style={{ marginLeft: 8 }}
                    />
                  </span>
                }
                key="stopped"
              />
            </Tabs>

            <Row justify="end" style={{ marginBottom: 16 }}>
              <Col>
                <Search
                  placeholder="Search by name/subject"
                  style={{ width: 300 }}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onSearch={handleSearch}
                  allowClear
                />
              </Col>
            </Row>

            <Table
              columns={columns}
              dataSource={emailCampaigns}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.totalEmailCampaigns,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `Total ${total} campaigns`,
              }}
              loading={loading}
              onChange={({ current, pageSize }) =>
                handlePaginationChange(current, pageSize)
              }
              rowKey={(record) => record._id}
              scroll={{ y: "calc(100vh - 380px)" }}
              tableLayout="fixed"
            />
          </Card>
        </Col>
      </Row>
    </Card>
  );
};

export default EmailCampaignsList;
